import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import {
  CheckCircle2,
  XCircle,
  Clock,
  Upload,
  Play,
  Cpu,
  HardDrive,
  Container,
  Zap,
  Cloud,
  Activity,
  AlertTriangle,
  Monitor,
  CloudLightning,
  Moon,
} from "lucide-react";
import { toast } from "sonner";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import SandboxLaunchStatus from "./SandboxLaunchStatus";
import SandboxRecoveryManager from "./SandboxRecoveryManager";
import { useAuth } from "../App";
import { useJupyterWorkspace } from "../contexts/JupyterWorkspaceContext";
import { cn } from "../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Copy, Terminal, Key, Eye, EyeOff } from "lucide-react";
import { useLocalRunCommandQuery, useAgentTokenQuery, useWakeAgentMutation, useSleepAgentMutation, useAgentSessionStatusQuery } from "../api/jupyterManagementApi";

interface SandboxSettings {
  mode: "UV" | "DOCKER" | "FARGATE";
  cpuLimit: string;
  memLimit: string;
  idleTimeout: number;
  imageName: string;
}

interface ChecklistItem {
  id: string;
  label: string;
  completed: boolean;
  icon: React.ReactNode;
}

interface SandboxLaunchPanelProps {
  projectId: string;
  onLaunch: (settings: SandboxSettings) => void;
  isLaunching?: boolean;
  onCancel?: () => void;
}

// Mock API functions that simulate backend calls but provide demo functionality
const fetchSandboxSettings = async (
  projectId: string
): Promise<SandboxSettings> => {
  // Simulate loading delay for demo
  await new Promise((resolve) => setTimeout(resolve, 500));

  return {
    mode: "DOCKER",
    cpuLimit: "0.5",
    memLimit: "2Gi",
    idleTimeout: 30,
    imageName: "jupyter/datascience-notebook:latest",
  };
};

const checkWorkspaceStatus = async (projectId: string) => {
  // Simulate loading delay for demo
  await new Promise((resolve) => setTimeout(resolve, 300));

  // In demo, randomly simulate different states
  const isReady = Math.random() > 0.3; // 70% chance it's ready

  return {
    is_ready: isReady,
    forkedAt: isReady
      ? new Date(Date.now() - Math.random() * 86400000).toISOString()
      : null,
    s3Prefix: `users/student-1/projects/${projectId}/`,
  };
};

const forkExercise = async (studentId: string, projectId: string) => {
  // Simulate processing time for demo
  await new Promise((resolve) => setTimeout(resolve, 1000));

  toast.success("Project forked successfully to your workspace!");

  return {
    success: true,
    message: "Project forked successfully",
    forkedAt: new Date().toISOString(),
  };
};

const pushToCloud = async (
  projectId: string,
  onProgress: (progress: number) => void
) => {
  // Simulate file push with progress updates for demo
  const totalSteps = 100;

  for (let i = 0; i <= totalSteps; i += 5) {
    onProgress(i);
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  toast.success("Files pushed successfully to cloud workspace!");

  return {
    success: true,
    message: "Files pushed successfully",
  };
};

export default function SandboxLaunchPanel({
  projectId,
  onLaunch,
  isLaunching = false,
  onCancel,
}: SandboxLaunchPanelProps) {
  const { user: currentUser } = useAuth();
  const { workspace, updateWorkspace } = useJupyterWorkspace();
  const isAdmin = currentUser?.role === "admin";
  const [settings, setSettings] = useState<SandboxSettings | null>(null);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([
    {
      id: "forked",
      label: "Project forked",
      completed: false,
      icon: <CheckCircle2 className="w-4 h-4" />,
    },
    {
      id: "pushed",
      label: "Files pushed to cloud",
      completed: false,
      icon: <Cloud className="w-4 h-4" />,
    },
    {
      id: "ready",
      label: "Sandbox ready",
      completed: false,
      icon: <Play className="w-4 h-4" />,
    },
  ]);
  const [isPushing, setIsPushing] = useState(false);
  const [pushProgress, setPushProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [launchFailed, setLaunchFailed] = useState(false);
  const [isInternalLaunching, setIsInternalLaunching] = useState(false);
  const [showLocalModal, setShowLocalModal] = useState(false);

  const { data: localRunData, isLoading: isLoadingLocalRun } = useLocalRunCommandQuery(projectId, showLocalModal);
  const wakeAgentMutation = useWakeAgentMutation();
  const sleepAgentMutation = useSleepAgentMutation();
  const { data: agentSessionStatus } = useAgentSessionStatusQuery(projectId, workspace.globalSandboxMode === "local" && workspace.workspaceType === "local");
  const agentStatus = agentSessionStatus?.status || "SLEEPING";

  const handleCopyCommand = () => {
    if (localRunData?.command) {
      navigator.clipboard.writeText(localRunData.command);
      toast.success("Command copied to clipboard!");
    }
  };

  const handleSleep = () => {
    sleepAgentMutation.mutate(projectId, {
      onSuccess: () => toast.info("Sleep signal sent to local agent")
    });
  };

  const getModeIcon = (mode: string) => {
    switch (mode) {
      case "UV":
        return <Zap className="w-4 h-4" />;
      case "DOCKER":
        return <Container className="w-4 h-4" />;
      case "FARGATE":
        return <Cloud className="w-4 h-4" />;
      default:
        return <Container className="w-4 h-4" />;
    }
  };

  const getModeColor = (mode: string) => {
    switch (mode) {
      case "UV":
        return "bg-yellow-100 text-yellow-800";
      case "DOCKER":
        return "bg-blue-100 text-blue-800";
      case "FARGATE":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load sandbox settings
        const sandboxSettings = await fetchSandboxSettings(projectId);
        setSettings(sandboxSettings);

        // Check workspace status
        const workspaceStatus = await checkWorkspaceStatus(projectId);

        // Update checklist based on status
        setChecklist((prev) =>
          prev.map((item) => {
            switch (item.id) {
              case "forked":
                return { ...item, completed: !!workspaceStatus.forkedAt };
              case "pushed":
                return { ...item, completed: workspaceStatus.is_ready };
              case "ready":
                return { ...item, completed: workspaceStatus.is_ready };
              default:
                return item;
            }
          })
        );
      } catch (error) {
        console.error("Failed to load sandbox data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId]);

  const handleForkProject = async () => {
    try {
      // In real implementation, get studentId from auth context
      await forkExercise("current-student-id", projectId);

      setChecklist((prev) =>
        prev.map((item) =>
          item.id === "forked" ? { ...item, completed: true } : item
        )
      );
    } catch (error) {
      console.error("Failed to fork project:", error);
    }
  };

  const handlePushToCloud = async () => {
    setIsPushing(true);
    setPushProgress(0);

    try {
      await pushToCloud(projectId, setPushProgress);

      setChecklist((prev) =>
        prev.map((item) =>
          item.id === "pushed" || item.id === "ready"
            ? { ...item, completed: true }
            : item
        )
      );
    } catch (error) {
      console.error("Failed to push to cloud:", error);
    } finally {
      setIsPushing(false);
    }
  };

  const handleLaunchComplete = (success: boolean, sandboxId?: string) => {
    setIsInternalLaunching(false);
    if (success) {
      setLaunchFailed(false);
      toast.success("Sandbox launched successfully!");
      // Call the parent's onLaunch callback or navigate as needed
    } else {
      setLaunchFailed(true);
      toast.error("Sandbox launch failed");
    }
  };

  const handleRetryLaunch = () => {
    setIsInternalLaunching(true);
    setLaunchFailed(false);
  };

  const handleRecoverySuccess = (sandboxId: string) => {
    setLaunchFailed(false);
    toast.success("Sandbox recovered successfully!");
    // Handle successful recovery
  };

  const handleRecoveryFailed = (reason: string) => {
    toast.error(`Recovery failed: ${reason}`);
  };

  const handleLaunch = async () => {
    if (settings) {
      setIsInternalLaunching(true);
      setLaunchFailed(false);
      
      try {
        // No longer need to resolve workspace URL upfront. 
        // Backend handles all target resolution.
        onLaunch(settings);
      } catch (err) {
        toast.error("Failed to initiate launch sequence.");
        setIsInternalLaunching(false);
      }

      // Simulate launch process - in real implementation this would be handled by the parent
      setTimeout(() => {
        handleLaunchComplete(Math.random() > 0.3); // 70% success rate for demo
      }, 8000);
    }
  };

  const toggleLocalMode = (checked: boolean) => {
    const newType = checked ? "local" : "cloud";
    updateWorkspace({ workspaceType: newType });
    toast.info(`Switched to ${newType.toUpperCase()} gateway`);
  };

  const allChecksPassed = checklist.every((item) => item.completed);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading Sandbox Settings...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show real-time launch status when launching
  if (isInternalLaunching || launchFailed) {
    return (
      <div className="space-y-6">
        <SandboxLaunchStatus
          isLaunching={isInternalLaunching}
          onLaunchComplete={handleLaunchComplete}
          onRetry={handleRetryLaunch}
          autoRetry={true}
          maxRetries={3}
        />

        {launchFailed && showAdvanced && (
          <SandboxRecoveryManager
            sandboxId={projectId}
            onRecoverySuccess={handleRecoverySuccess}
            onRecoveryFailed={handleRecoveryFailed}
          />
        )}

        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setIsInternalLaunching(false);
                  setLaunchFailed(false);
                }}
              >
                Cancel
              </Button>

              {launchFailed && (
                <Button
                  variant="outline"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center gap-2"
                >
                  <Activity className="w-4 h-4" />
                  {showAdvanced ? "Hide" : "Show"} Advanced Recovery
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <Card className="dark:bg-[#161b22] dark:border-[#30363d]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 dark:text-[#f0f6fc]">
          <Container className="w-5 h-5" />
          Sandbox Environment
        </CardTitle>
        {isAdmin && workspace.globalSandboxMode === "local" && (
          <div className="flex items-center space-x-2 bg-gray-50 dark:bg-[#0d1117] p-2 rounded-lg border dark:border-[#30363d]">
            <div className="flex flex-col">
              <span className="text-xs font-medium dark:text-[#f0f6fc]">Local Agent Mode</span>
              <span className="text-[10px] text-gray-500 uppercase">Phase 2 Portability</span>
            </div>
            <Switch 
              checked={workspace.workspaceType === "local"}
              onCheckedChange={toggleLocalMode}
            />
          </div>
        )}

        {workspace.globalSandboxMode === "local" && workspace.workspaceType === "local" && (
          <div className="mt-4 flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "flex-1 flex items-center justify-center gap-2 font-medium transition-all",
                agentStatus === "CONNECTED" ? "bg-green-50 text-green-700 border-green-200" : 
                agentStatus === "WAKE_PENDING" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
                "bg-blue-50 text-blue-700 border-blue-100 dark:bg-[#0d1117] dark:border-[#30363d]"
              )}
              onClick={() => wakeAgentMutation.mutate(projectId)}
              disabled={wakeAgentMutation.isPending || agentStatus === "CONNECTED"}
            >
              <CloudLightning className={cn("w-4 h-4", (wakeAgentMutation.isPending || agentStatus === "WAKE_PENDING") && "animate-pulse")} />
              {wakeAgentMutation.isPending ? "Sending Wake..." : 
               agentStatus === "WAKE_PENDING" ? "Connecting..." :
               agentStatus === "CONNECTED" ? "Agent Ready" : 
               "Wake Local Agent"}
            </Button>

            {(agentStatus === "CONNECTED" || agentStatus === "WAKE_PENDING") && (
              <Button
                variant="outline"
                size="sm"
                className="flex items-center justify-center gap-2 font-medium border-red-100 text-red-600 hover:bg-red-50 dark:bg-[#0d1117] dark:border-[#30363d]"
                onClick={handleSleep}
                disabled={sleepAgentMutation.isPending}
              >
                <Moon className={cn("w-4 h-4", sleepAgentMutation.isPending && "animate-pulse")} />
                {sleepAgentMutation.isPending ? "Sleeping..." : "Sleep"}
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Sandbox Configuration Display */}
        {settings && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 dark:bg-[#0d1117] rounded-lg border dark:border-[#30363d]">
            <div className="flex items-center gap-2">
              <Badge
                variant="secondary"
                className={`${getModeColor(
                  settings.mode
                )} flex items-center gap-1`}
              >
                {getModeIcon(settings.mode)}
                {settings.mode}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Cpu className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm dark:text-gray-300">{settings.cpuLimit} CPU</span>
            </div>
            <div className="flex items-center gap-2">
              <HardDrive className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm dark:text-gray-300">{settings.memLimit} RAM</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm dark:text-gray-300">{settings.idleTimeout}min idle</span>
            </div>
          </div>
        )}

        {/* Pre-launch Checklist */}
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900 dark:text-[#f0f6fc]">Pre-launch Checklist</h4>
          <div className="space-y-2">
            {checklist.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 p-2 rounded-md hover:bg-gray-50 dark:hover:bg-[#21262d] transition-colors"
              >
                <div
                  className={`${
                    item.completed ? "text-green-600 dark:text-green-500" : "text-gray-400 dark:text-gray-600"
                  }`}
                >
                  {item.completed ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : (
                    <XCircle className="w-5 h-5" />
                  )}
                </div>
                <span
                  className={`${
                    item.completed ? "text-gray-900 dark:text-[#c9d1d9]" : "text-gray-500 dark:text-[#8b949e]"
                  }`}
                >
                  {item.label}
                </span>
                {item.id === "forked" && !item.completed && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleForkProject}
                    className="ml-auto dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
                  >
                    Fork Now
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Push to Cloud Section */}
        <div className="space-y-3">
          {isPushing && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-[#8b949e]">
                  Pushing files to cloud...
                </span>
                <span className="text-sm font-medium dark:text-[#f0f6fc]">{pushProgress}%</span>
              </div>
              <Progress value={pushProgress} className="w-full h-2" />
            </div>
          )}

          <div className="flex gap-3">
            <Button
              onClick={handlePushToCloud}
              disabled={
                isPushing ||
                checklist.find((item) => item.id === "pushed")?.completed
              }
              variant="outline"
              className="flex items-center gap-2 dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
            >
              <Upload className="w-4 h-4" />
              {isPushing ? "Pushing..." : "Push to Cloud"}
            </Button>

            {workspace.globalSandboxMode === "local" && (
              <Button
                onClick={() => setShowLocalModal(true)}
                variant="outline"
                disabled={!allChecksPassed || isLaunching || isInternalLaunching}
                className="flex items-center gap-2 dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
              >
                <Terminal className="w-4 h-4" />
                Run Locally
              </Button>
            )}

            <div className="flex gap-2">
              <Button
                onClick={handleLaunch}
                disabled={!allChecksPassed || isLaunching || isInternalLaunching}
                className="bg-bits-blue hover:bg-bits-blue/90 dark:bg-[#f0883e] dark:hover:bg-[#e0772d] dark:text-white flex items-center gap-2 transition-colors flex-1"
              >
                <Play className="w-4 h-4" />
                {isLaunching || isInternalLaunching
                  ? "Launching..."
                  : "Launch Sandbox"}
              </Button>

              {(isLaunching || isInternalLaunching) && onCancel && (
                <Button
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  onClick={onCancel}
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Enhanced Status Information */}
        {!allChecksPassed && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-[#261c02] border border-amber-200 dark:border-[#4d3d02] rounded-lg">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-200">
              <p className="font-medium">
                Complete checklist to launch sandbox
              </p>
              <p>
                All items must be completed before launching your environment.
              </p>
            </div>
          </div>
        )}

        {/* Image Information */}
        {settings && (
          <div className="p-3 bg-blue-50 dark:bg-[#0d1117] rounded-md border border-blue-200 dark:border-[#30363d]">
            <div className="flex items-center gap-2">
              <Container className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-900 dark:text-blue-300">Image:</span>
              <span className="text-sm text-blue-700 dark:text-blue-200 font-mono">
                {settings.imageName}
              </span>
            </div>
          </div>
        )}

        {/* Local Run Modal */}
        <Dialog open={showLocalModal} onOpenChange={setShowLocalModal}>
          <DialogContent className="sm:max-w-xl dark:bg-[#161b22] dark:border-[#30363d]">
            <DialogHeader>
              <DialogTitle className="dark:text-[#f0f6fc]">Run Sandbox Locally</DialogTitle>
              <DialogDescription className="dark:text-[#8b949e]">
                Run the exact same Jupyter environment on your own machine using Docker. This ensures zero latency and no timeouts.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {isLoadingLocalRun ? (
                <div className="p-4 bg-gray-50 dark:bg-[#0d1117] rounded-md animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                </div>
              ) : localRunData ? (
                <>
                  <div className="bg-gray-900 rounded-md p-4 overflow-x-auto relative group">
                    <pre className="text-gray-100 text-sm whitespace-pre-wrap font-mono relative z-10 break-all p-3">
                      {localRunData.command}
                    </pre>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="absolute top-2 right-2 text-gray-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity z-20"
                      onClick={handleCopyCommand}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Note: Your temporary access token is embedded into this command. Do not share it with others.
                  </p>
                </>
              ) : (
                <p className="text-red-500 text-sm">Failed to generate command. Please try again.</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
